import React, { useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Col, Row, Button, Dropdown, Modal, message } from "antd";
import get from "lodash.get";
import { openJobCreatePanel } from "@/common/open-job-create-panel";
import {
  showPanel,
  setSelectedTableItems,
  clearFilter,
  setFilter,
} from "@/pages/shared/shared.reducer";
import { setValue as setDashboardValue } from "@/pages/dashboard/dashboard.slice";
import panels from "@/constants/panels";
import { openErrorNotification, openSuccessNotification } from "@/notification";
import {
  haveAbilityToChangeCategory,
  haveAbilityToDownloadSources,
  haveAbilityToChangeSites,
  haveAbilityCreateJobByList,
  haveAbilityRetireAndUnretireDocuments,
  haveAbilityExportDocuments,
  haveAbilityToDownloadPdfs,
  haveAbilityDeleteDocuments,
  haveAbilityOpenDocuments,
  haveAbilityRetireAndUnretireDocument,
} from "@/pages/login/permission-service";
import {
  loadAllCartCategories,
  loadAllCartSubCategories,
  loadAllCategories,
  loadAllSubCategories,
} from "@/common/document-filters/document-filters-reducer";
import { setWorkItems } from "@/pages/update-metadata/reducer";
import { Hint } from "@/common/hint";
import { createDashboardCartActions } from "@/common/document-filters/select-footer.cart-actions";
import { resolveCartFileIdsToRemove } from "@/common/document-filters/cart-removal-utils";
const pdfLineIcon = "/images/pdf-line.png";
import {
  dashboardApi,
  useDownloadDocumentFileMutation,
  useDownloadBookMutation,
} from "@/pages/dashboard/dashboard.reducer";
import useFavouritesManager from "@/hooks/useFavouritesManager";
import "./select-footer.less";
const uploadLineIcon = "/images/upload-line-2.png";

const { confirm } = Modal;

const NO_ITEMS = [];

const DashboardTableActionsComponent = (props) => {
  const {
    showRemoveFromFavourites,
    showMoreActions,
    width,
    refetch,
    isCartTab,
    updateTotalDocumentCount,
    forceRender,
    tableActionsRef,
  } = props;
  const isMobile = width < 768;
  const dispatch = useDispatch();

  const permissions = useSelector((state) => state.userDetails.permissions);
  const canAddWorkPacks = useSelector((state) =>
    get(state.userDetails, "setting.canAddBookAndEditWorkpacks")
  );
  const canDoBulkMetadataUtilityEdits = useSelector(
    (state) =>
      get(state.userDetails, "setting.canDoBulkMetadataUtilityEdits") === true
  );
  const siteId = useSelector((state) =>
    get(state.shared, "dashboardTable.filters.siteId")
  );
  const cartDocumentIds = useSelector(
    (state) => state.cartManagement.dashboardCartDocumentIds || NO_ITEMS
  );
  const dashboardSelectedItems = useSelector(
    (state) => get(state.shared, "dashboardTable.selectedItems") || NO_ITEMS
  );
  const cartSelectedItems = useSelector(
    (state) => get(state.shared, "cartManagementTable.selectedItems") || NO_ITEMS
  );
  const currentDocumentStatusId = useSelector(
    (state) =>
      get(state.shared, "dashboardTable.filters.documentStatusId") || 1
  );
  const currentCartDocumentStatusId = useSelector(
    (state) =>
      get(state.shared, "cartManagementTable.filters.documentStatusId") || 1
  );
  const showAllRevision = useSelector(
    (state) =>
      get(state.shared, "dashboardTable.filters.showAllRevision") || false
  );

  const { emptyCart, addToCart, deleteFromCart } = useMemo(
    () => createDashboardCartActions(dispatch),
    [dispatch]
  );

  const unretireDocuments = useCallback(
    (
      documents,
      unretireSiteId,
      documentStatusId,
      cartDocumentStatusId,
      refetchTable
    ) => {
      dispatch(
        dashboardApi.endpoints.unretireDocument.initiate({
          siteId: unretireSiteId,
          documents,
          refetch: () => {
            dispatch(setSelectedTableItems("dashboardTable", [], []));
            dispatch(loadAllCategories(unretireSiteId, documentStatusId));
            dispatch(
              loadAllCartCategories(unretireSiteId, cartDocumentStatusId)
            );
            dispatch(loadAllSubCategories(unretireSiteId, documentStatusId));
            dispatch(
              loadAllCartSubCategories(unretireSiteId, cartDocumentStatusId)
            );
            refetchTable();
          },
        })
      );
    },
    [dispatch]
  );

  const exportDocuments = useCallback(
    (idType, ids, suffix) => {
      dispatch(
        dashboardApi.endpoints.exportDocuments.initiate({ idType, ids, suffix })
      );
    },
    [dispatch]
  );

  const showJobPanel = useCallback(
    (documents) => openJobCreatePanel(dispatch, documents),
    [dispatch]
  );

  const showUpdateCategoriesPanel = useCallback(() => {
    dispatch(setDashboardValue({ key: "categoryLoading", value: true }));
    dispatch(showPanel(panels.changeCategory));
    setTimeout(() => {
      dispatch(setDashboardValue({ key: "categoryLoading", value: false }));
    }, 200);
  }, [dispatch]);

  const showChangeSitesPanel = useCallback(() => {
    dispatch(showPanel(panels.changeSites));
  }, [dispatch]);

  const clearCartFilter = useCallback(
    (cartSiteId) => {
      dispatch(clearFilter("cartManagementTable"));
      dispatch(setFilter("cartManagementTable", "siteId", cartSiteId));
      dispatch(setFilter("cartManagementTable", "documentStatusId", 1));
    },
    [dispatch]
  );

  const showAddChangeCommentPanel = useCallback(
    (value) => {
      dispatch(
        setDashboardValue({
          key: `${panels.addChangeComment}.record`,
          value,
        })
      );
      dispatch(showPanel(panels.addChangeComment));
    },
    [dispatch]
  );

  const showRetireDocumentPanel = useCallback(
    (value) => {
      dispatch(
        setDashboardValue({ key: `${panels.retireDocument}.record`, value })
      );
      dispatch(showPanel(panels.retireDocument));
    },
    [dispatch]
  );

  const showRollbackDocumentPanel = useCallback(
    (value) => {
      dispatch(
        setDashboardValue({ key: `${panels.rollbackDocument}.record`, value })
      );
      dispatch(showPanel(panels.rollbackDocument));
    },
    [dispatch]
  );

  const showDeleteDocumentPanel = useCallback(
    (value) => {
      dispatch(
        setDashboardValue({ key: `${panels.deleteDocument}.record`, value })
      );
      dispatch(showPanel(panels.deleteDocument));
    },
    [dispatch]
  );

  const openCreateWorkpackPanel = useCallback(
    (workpackSiteId) => {
      dispatch(
        dashboardApi.endpoints.availableForDistributionDashboardSites.initiate(
          workpackSiteId
        )
      );
      dispatch(showPanel(panels.createWorkpack));
    },
    [dispatch]
  );

  const handleSetWorkItems = useCallback(
    (selected) => {
      dispatch(setWorkItems(selected));
    },
    [dispatch]
  );
  
  const currentDashboard = useSelector(
    (state) => state.dashboardPage.currentDashboard
  );

  const [downloadDocumentFile] = useDownloadDocumentFileMutation();
  const [downloadBook] = useDownloadBookMutation();
  const [messageApi, contextHolder] = message.useMessage();

  const { batchToggleFavourites } =
    useFavouritesManager({
      updateTotalDocumentCount,
      refetch,
      siteId,
      updateOnlyFavourites: true,
    });

  const countOfSelectedItems = isCartTab
    ? cartSelectedItems
      ? cartSelectedItems.length
      : 0
    : dashboardSelectedItems
    ? dashboardSelectedItems.length
    : 0;
  const selectedItems = isCartTab
    ? cartSelectedItems
    : dashboardSelectedItems;
  const canUpdateCategories = currentDashboard !== "allSitesDashboard" && haveAbilityToChangeCategory(
    permissions,
    selectedItems
  );
  const canChangeSites = currentDashboard !== "allSitesDashboard" && haveAbilityToChangeSites(permissions, selectedItems);
  const canDownloadOriginals = haveAbilityToDownloadSources(
    permissions,
    selectedItems
  );
  const canRetireOrUnretire = haveAbilityRetireAndUnretireDocuments(
    permissions,
    selectedItems
  );
  const canCreateJob = currentDashboard !== "allSitesDashboard" && haveAbilityCreateJobByList(permissions, selectedItems);
  const canExportDocuments = currentDashboard !== "allSitesDashboard" && haveAbilityExportDocuments(
    permissions,
    selectedItems
  );
  const canDownloadPdfs = haveAbilityToDownloadPdfs(permissions, selectedItems);
  const canDeleteDocuments = currentDashboard !== "allSitesDashboard" && haveAbilityDeleteDocuments(
    permissions,
    selectedItems || []
  );
  const canUpdateMetadata =
    currentDashboard !== "allSitesDashboard" &&
    selectedItems &&
    selectedItems.filter((x) => haveAbilityOpenDocuments(permissions, x));
  const showRetireButton =
    currentDashboard !== "allSitesDashboard" &&
    selectedItems &&
    selectedItems.filter(
      (x) =>
        haveAbilityRetireAndUnretireDocument(permissions, x) &&
        x.documentStatusId == 3
    ).length == 0 &&
    selectedItems.filter((x) => x.documentStatusId == 1).length > 0;
  const showRollbackButton = currentDashboard !== "allSitesDashboard" && haveAbilityDeleteDocuments(
    permissions,
    selectedItems
  );
  const showUnretireButton = currentDashboard !== "allSitesDashboard" &&
    selectedItems &&
    selectedItems.filter(
      (x) =>
        haveAbilityRetireAndUnretireDocument(permissions, x) &&
        x.documentStatusId == 3
    ).length > 0;
  const showAddToFavouritesButton = currentDashboard !== "allSitesDashboard" &&
    selectedItems &&
    selectedItems.filter((x) => x.favourite == false).length > 0;
  const haveAbilityAddWorkpack = currentDashboard !== "allSitesDashboard" && selectedItems
    ? selectedItems.filter(
        (x) =>
          x.documentRevisionStatus != "Retired" &&
          x.documentRevisionStatus != "Reserved" &&
          !x.restrictAdditionOfTheDocumentToBooks &&
          !x.noDocumentCanBeAddedToWorkspace
      )
    : [];
  const handleAddToFavourites = useCallback(
    async (items) => {
      try {
        await batchToggleFavourites(items, true);
      } catch (error) {
        console.error("Failed to add to favourites:", error);
      }
    },
    [batchToggleFavourites]
  );

  const handleRemoveFromFavourites = useCallback(
    async (items) => {
      try {
        await batchToggleFavourites(items, false);
      } catch (error) {
        console.error("Failed to remove from favourites:", error);
      }
    },
    [batchToggleFavourites]
  );

  const downloadSelected = useCallback(
    (selectedItems, documentTypeId) => {
      let ids = [];
      let fileName;
      let revision;
      let documentNumber;
      selectedItems.forEach((item) => {
        if (item.documentFile) {
          const {
            documentFileId,
            sourceFileName,
            publishedFileName,
            revisionNumber,
          } = item.documentFile;
          fileName = documentTypeId == 1 ? sourceFileName : publishedFileName;
          revision = revisionNumber;
          documentNumber = item.documentNumber;
          if (documentFileId) {
            ids.push(documentFileId);
          }
        }
      });
      downloadDocumentFile({
        documentFileIds: ids,
        documentTypeId,
        fileName,
        revision,
        documentNumber,
      });
    },
    [downloadDocumentFile]
  );

  const showQuestionUnretire = () => {
    confirm({
      title: "Are you sure you want to unretire the selected document(s)?",
      okText: "Confirm Unretire",
      cancelText: "Cancel",
      onOk() {
        unretireDocuments(
          selectedItems,
          siteId,
          currentDocumentStatusId,
          currentCartDocumentStatusId,
          refetch
        );
      },
    });
  };

  const showQuestionDeleteCartList = () => {
    confirm({
      title: "Are you sure you want to empty cart?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        emptyCart(updateTotalDocumentCount, refetch);
      },
    });
  };

  const handleAddToCart = async () => {
    const cartActionMessageKey = "dashboard-add-to-cart";

    messageApi.open({
      key: cartActionMessageKey,
      type: "loading",
      content: "Adding to cart...",
      duration: 0,
    });

    let alreadyInCart = [];
    let addedDocs = [];
    let newCartItems = cartDocumentIds.length > 0 ? [...cartDocumentIds] : [];
    let fileIdsToAdd = [];

    try {
      if (showAllRevision) {
        // When showing all revisions, each selected row is a specific revision
        selectedItems.forEach((item) => {
          const fileId = item.documentFile?.documentFileId || item.documentFileId;
          const { documentNumber } = item;
          if (newCartItems.includes(fileId)) {
            alreadyInCart.push(documentNumber);
          } else {
            fileIdsToAdd.push(fileId);
            addedDocs.push(documentNumber);
            newCartItems.push(fileId);
          }
        });
      } else {
        // When show all revisions is off, resolve all revision fileIds for each selected document
        const uniqueDocumentIds = [...new Set(selectedItems.map((item) => item.documentId))];
        try {
          const result = await dispatch(
            dashboardApi.endpoints.resolveDocumentFileIds.initiate({
              documentIds: uniqueDocumentIds,
              siteId,
            })
          ).unwrap();
          const allFileIds = result || [];

          // Track which documents were already fully in cart vs newly added
          const docNumberMap = {};
          selectedItems.forEach((item) => {
            docNumberMap[item.documentId] = item.documentNumber;
          });

          // Check if all file IDs for a document are already in cart
          uniqueDocumentIds.forEach((docId) => {
            const docFileIds = allFileIds.filter((fId) => !newCartItems.includes(fId));
            if (docFileIds.length === 0 && allFileIds.length > 0) {
              alreadyInCart.push(docNumberMap[docId]);
            }
          });

          allFileIds.forEach((fId) => {
            if (!newCartItems.includes(fId)) {
              fileIdsToAdd.push(fId);
              newCartItems.push(fId);
            }
          });

          // Determine which doc numbers were actually added
          selectedItems.forEach((item) => {
            const fileId = item.documentFile?.documentFileId || item.documentFileId;
            if (fileIdsToAdd.includes(fileId) && !addedDocs.includes(item.documentNumber)) {
              addedDocs.push(item.documentNumber);
            }
          });
        } catch (error) {
          console.error("Error resolving document file IDs:", error);
          openErrorNotification("Failed to add documents to cart. Please try again.");
          return;
        }
      }

      if (fileIdsToAdd.length > 0) {
        addToCart(fileIdsToAdd, updateTotalDocumentCount);
      }

      if (alreadyInCart.length > 0) {
        openErrorNotification(
          <div>
            <h6>Documents already in the Cart:</h6>
            <ul>
              {alreadyInCart.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        );
        clearCartFilter(siteId);
      }

      if (addedDocs.length > 0) {
        openSuccessNotification(
          <div>
            <h6>Documents added to the Cart:</h6>
            <ul>
              {[...new Set(addedDocs.map((x) => x))].map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        );
      }
    } finally {
      messageApi.destroy(cartActionMessageKey);
    }
  };

  const removeFromCart = async () => {
    const cartActionMessageKey = "dashboard-remove-from-cart";

    messageApi.open({
      key: cartActionMessageKey,
      type: "loading",
      content: "Removing from cart...",
      duration: 0,
    });

    let removedItems = [];
    let fileIdsToRemove = [];

    try {
      fileIdsToRemove = await resolveCartFileIdsToRemove({
        selectedItems,
        showAllRevision,
        resolveDocumentFileIds: async (documentIds) => {
          const result = await dispatch(
            dashboardApi.endpoints.resolveDocumentFileIds.initiate({
              documentIds,
              siteId,
            })
          ).unwrap();

          return result || [];
        },
      });

      removedItems = [...new Set(selectedItems.map((item) => item.documentNumber).filter(Boolean))];

      if (fileIdsToRemove.length > 0) {
        deleteFromCart(fileIdsToRemove, updateTotalDocumentCount);
      }
      if (removedItems && removedItems.length > 0) {
        openSuccessNotification(
          <div>
            <h6>Documents removed from the Cart:</h6>
            <ul>
              {[...new Set(removedItems.map((x) => x))].map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        );
      }
      refetch();
    } catch (error) {
      console.error("Error resolving cart file IDs for removal:", error);
      openErrorNotification("Failed to remove documents from cart. Please try again.");
    } finally {
      messageApi.destroy(cartActionMessageKey);
    }
  };

  const handleDownloadBook = () => {
    if (countOfSelectedItems > 0) {
      let ids = [];
      const documentsLocked = selectedItems.filter(
        (item) =>
          item.documentFile && item.documentFile.publishedFileLocked == true
      );
      const documentForDownload = selectedItems.filter(
        (item) =>
          item.documentFile && item.documentFile.publishedFileLocked != true
      );
      const canDownloadPdfs = haveAbilityToDownloadPdfs(
        permissions,
        documentForDownload
      );
      ids = canDownloadPdfs.map((item) => item.documentFile && item.documentFile?.documentFileId);
      
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const yyyy = today.getFullYear();
      const hh = String(today.getHours()).padStart(2, "0");
      const m = String(today.getMinutes()).padStart(2, "0");
      const s = String(today.getSeconds()).padStart(2, "0");

      if (ids.length > 0) {
        downloadBook({ ids, name: `DocumentsBook-${hh}${m}${s}_${dd}${mm}${yyyy}.pdf` });
      }

      if (documentsLocked && documentsLocked.length > 0) {
        openErrorNotification(
          <div>
            <h6>Locked PDF files cannot be downloaded</h6>
            <li>
              {documentsLocked.map((item, index) => (
                <ul key={index} className="ps-1 mb-1">
                  {item.documentNumber}
                </ul>
              ))}
            </li>
          </div>
        );
      }
    }
  };

  const getMenuItems = () => {
        let items = [
          (!isCartTab && currentDashboard !== "allSitesDashboard" && width < 900) ? {
                key: '0',
                onClick: () => handleAddToCart(),
                label: (
                    <Button type="link" className="display-center small-gap">
                        <i className="ri-shopping-basket-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                        Add to Cart
                    </Button>
                ),
            } : null,
          (showAddToFavouritesButton && (width < 1600)) ? {
                key: '1',
                onClick: () => handleAddToFavourites(selectedItems),
                label: (
                    <Button type="link" className="display-center small-gap" >
                        <i className="ri-star-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                        Add to Favourites
                    </Button>
                ),
            } : null,
          (showRemoveFromFavourites && isMobile) ? {
                key: '2',
                onClick: () => handleRemoveFromFavourites(selectedItems),
                label: (
                    <Button type="link" className="display-center small-gap" >
                        <i className="ri-bookmark-2-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                        Remove from Favourites
                    </Button>
                ),
              } : null,
            (isCartTab && isMobile) ? {
                key: '3',
                onClick: () => removeFromCart(),
                label: (
                    <Button type="link" className="display-center small-gap" >
                        <i className="ri-shopping-basket-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                        Remove from Cart
                    </Button>
                ),
              } : null,      
            (canDownloadOriginals && canDownloadOriginals.length > 0) && width < 1080 ? {
                key: '5',
                onClick: () => downloadSelected(canDownloadOriginals, 1),
                label: (
                    <Button type="link" className="display-center small-gap">
                        <i className="ri-file-download-line" style={{ color: "#0860DB" }} ></i>
                        <span>Download <b>Natives</b></span>
                    </Button>
                ),
              } : null,      
            (canDownloadPdfs && canDownloadPdfs.length > 0 && isMobile) ? {                
                key: '4',
                onClick: () => downloadSelected(canDownloadPdfs, 2),
                label: (
                    <Button type="link" className="display-center small-gap">
                        <img src={pdfLineIcon} alt="PDF" className="pdf-icon" />
                        <span>Download <b>PDF</b></span>
                    </Button>
                ),
              } : null,
            (canDownloadPdfs && canDownloadPdfs.length > 0) && width < 1800 ? {
                key: '6',
                onClick: () => handleDownloadBook(),
                label: (
                    <Button type="link" className="display-center small-gap">
                        <i className="ri-book-line" style={{ color: "var(--dms-color-book-action)" }}></i>
                        <span>Download as <b>Book</b></span>
                    </Button>
                ),
            } : null,
            currentDashboard !== "allSitesDashboard" ? { 
              key: '7',
              onClick: () => showAddChangeCommentPanel(selectedItems),
              label: (
                  <Button type="link" className="display-center small-gap">
                      <i className="ri-chat-new-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                      Add change comment
                  </Button>
              )
            } : null,
          (canAddWorkPacks && haveAbilityAddWorkpack && haveAbilityAddWorkpack.length > 0) ? {
                key: '8',
                onClick: () => openCreateWorkpackPanel(siteId),
                label: (
                    <Hint icon='ri-question-line' className="document-actions-menu-help"
                        popoverText={
                            <>
                                Creating a Collection allows for a dynamic set of documents to be set on any Distribution Dashboard.<br></br>
                                These collections allow users to easily download a combined set of the latest drawings.<br></br>
                                A good example is to create entries on the Books Dashboard for plant items on your site.<br></br>
                                No more having users accidentally only downloading 4/5 of the drawings because the wrong search terms were used.
                            </>
                        }
                        placement="right"
                        width='400px'
                        >
                        <Button type="link" className="display-center small-gap">
                            <i className="ri-file-copy-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                            <div>Create Collection</div>
                        </Button>  
                    </Hint>
                ),
            } : null,
          (canUpdateCategories && canUpdateCategories.length > 0) ? {
                key: '9',
                onClick: () => showUpdateCategoriesPanel(),
                label: (
                    <Button type="link" className="display-center small-gap">
                        <i className="ri-refresh-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                        Change Categories
                    </Button>
                ),
            } : null,
          (canCreateJob && canCreateJob.length > 0) ? {
                key: '10',
                onClick: () => showJobPanel(selectedItems),
                label: (
                    <Button type="link" className="display-center small-gap">
                        <i className="ri-hammer-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                        Create Job
                    </Button>
                ),
            } : null,
          (canChangeSites && canChangeSites.length > 0) ? {
                key: '11',
                onClick: () => showChangeSitesPanel(),
                label: (
                    <Button type="link" className="display-center small-gap">
                        <i className="ri-file-code-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                        Change Sites
                    </Button>
                ),
            } : null,
          (showRollbackButton && canDeleteDocuments && canDeleteDocuments.length > 0) ? {
                key: '12',
                onClick: () => showRollbackDocumentPanel(selectedItems),
                label: (
                    <Hint icon='ri-question-line' className="document-actions-menu-help"
                        popoverText={
                            <>
                                Rollback of each selected document will delete its Current revision and make its highest Superseded revision Current.<br></br>
                                Rollback will delete any document without a superseded revision, or you may skip rollback for these.
                            </>
                        }
                        placement="right"
                        width='400px'
                        >
                        <Button type="link">
                            <i className="ri-file-history-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                            Rollback Revisions
                        </Button> 
                    </Hint>
                ),
            } : null,
          (canRetireOrUnretire && showRetireButton && canRetireOrUnretire.length > 0) ? {
                key: '13',
                onClick: () => showRetireDocumentPanel(selectedItems),
                label: (
                    <Button className="display-center small-gap" 
                        style={{ color: "var(--dms-color-warning)" }}
                        type="link">
                        <i className="ri-file-excel-line" ></i>
                        Retire Documents
                    </Button>
                ),
            } : null,
          (canRetireOrUnretire && showUnretireButton && canRetireOrUnretire.length > 0) ? {
                key: '14',
                onClick: () => showQuestionUnretire(),
                label: (
                    <Button className="display-center small-gap" 
                        style={{ color: "var(--dms-color-warning)" }} 
                        type="link">
                        <i className="ri-arrow-go-back-line"></i>
                            Unretire Documents
                    </Button>
                ),
            } : null,
          (!showAllRevision && canDeleteDocuments && canDeleteDocuments.length > 0) ? {
                key: '15',
                onClick: () => showDeleteDocumentPanel(selectedItems),
                label: (
                    <Button type="link" className="display-center small-gap"
                      style={{ color: "var(--dms-color-danger)" }}  >
                        <i className="ri-delete-bin-line" />
                        <div>Delete Documents</div>
                    </Button>
                ),
            } : null,
          (showAllRevision && canDeleteDocuments && canDeleteDocuments.length > 0) ? {
                key: '16',
                onClick: () => showDeleteDocumentPanel(selectedItems),
                label: (
                    <Button type="link" className="display-center small-gap " 
                      style={{ color: "var(--dms-color-danger)" }} >
                        <i className="ri-delete-bin-line" />
                        <div>Delete selected Revisions</div>
                    </Button>
                ),
            } : null,
          (canExportDocuments && canExportDocuments.length > 0) ? {
                key: '17',
                onClick: () => exportDocuments(
                    showAllRevision ? "documentFileIds" : "documentIds",
                    showAllRevision ? canExportDocuments.map(item => item.documentFileId) : canExportDocuments.map(item => item.documentId),
                    showAllRevision ? "SelectedRevisions" : "AllRevisions"
                ),
                label: (
                    <Button type="link" className='small-gap'>
                        <img src={uploadLineIcon} className="upload-icon" alt="Export"
                        />
                        <div>{!showAllRevision ? "Export all Revisions" : "Export selected Revisions"}</div>
                    </Button>
                ),
            } : null,
          (canExportDocuments && canExportDocuments.length > 0) && !showAllRevision ? {
                key: '18',
                onClick: () => exportDocuments("documentFileIds", canExportDocuments.map(item => item.documentFileId), "CurrentOnly"),
                label: (
                    <Button type="link">
                        <i className="ri-upload-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                        <div>Export current Revision/s</div>
                    </Button>
                ),
            } : null,
          (canDoBulkMetadataUtilityEdits && canUpdateMetadata && canUpdateMetadata.length > 0) ? {
                key: '19',
                onClick: () => handleSetWorkItems(canUpdateMetadata),
                label: (
                    <Link to="/update-metadata" className="document-actions-menu-link display-center small-gap">
                        <i className="ri-edit-box-line" style={{ color: "var(--dms-color-text-muted)" }}></i>
                        <span>Bulk Metadata Edit</span>
                    </Link>
                ),
            } : null
        ].filter(item => item !== null);

    const itemsWithDividers = items.reduce((acc, item, index, array) => {
      acc.push(item);
      if (index < array.length - 1) {
        acc.push({ type: "divider", key: `divider-${index}` });
      }
      return acc;
    }, []);

    return itemsWithDividers;
  };

  if (countOfSelectedItems > 0 || forceRender) {
    return (
      <>
        {contextHolder}
        <div
          style={{ zIndex: 2, width: "100%" }}
          className={
            countOfSelectedItems
              ? "footer-for-select bottom-0"
              : "footer-for-select bottom-0 zero-selected"
          }
        >
          <div ref={tableActionsRef} className="row">
            <Row className="footer-for-select-row" style={{ padding: 0 }}>
              <Col>
                <div
                  className={"selected"}
                  style={{ fontSize: "12px", gap: "6px", whiteSpace: "nowrap" }}
                >
                  {countOfSelectedItems > 500 ? (
                    <Hint icon='ri-error-warning-line'
                      popoverText={
                        "Selected large amount of items, any actions will take a while"
                      }
                      placement="top"
                      iconStyle={{ color: "var(--dms-color-danger)" }}
                    >
                      <div
                        style={{
                          color: `${
                            countOfSelectedItems > 500 ? "var(--dms-color-danger)" : "var(--dms-color-text)"
                          }`,
                        }}
                      >
                        {countOfSelectedItems}
                      </div>
                    </Hint>
                  ) : (
                    countOfSelectedItems
                  )}
                  <div className="mb-0">
                    {" "}
                    item selected: {!countOfSelectedItems && " to take actions"}
                  </div>
                </div>
              </Col>
              {isMobile && (
                <Dropdown
                  placement="top"
                  className="white-button"
                  menu={{ items: getMenuItems(), className: "document-actions-menu" }}
                >
                  <Button className="white-button">
                    Select action
                    <i className="ri-arrow-down-s-line"></i>
                  </Button>
                </Dropdown>
              )}
              <Col>
                {width >= 768 && (
                  <div className="actions-section">
                    {!isCartTab && currentDashboard !== "allSitesDashboard" && width >= 900 && (
                      <Button onClick={handleAddToCart} className="common-button white-button">
                        <i
                          className="ri-shopping-basket-line"
                          style={{ color: "var(--dms-color-text-muted)" }}
                        ></i>
                        Add to Cart
                      </Button>
                    )}
                    {showAddToFavouritesButton && width >= 1600 && (
                      <Button
                        onClick={() => handleAddToFavourites(selectedItems)}
                        className="white-button"
                      >
                        <i
                          className="ri-star-line"
                          style={{ color: "var(--dms-color-text-muted)" }}
                        ></i>
                        Add to Favourites
                      </Button>
                    )}
                    {showRemoveFromFavourites && (
                      <Button
                        onClick={() => handleRemoveFromFavourites(selectedItems)}
                        className="white-button"
                      >
                        <i
                          className="ri-bookmark-2-line"
                          style={{ color: "var(--dms-color-text-muted)" }}
                        ></i>
                        Remove from Favourites
                      </Button>
                    )}
                    {isCartTab && (
                      <Button onClick={removeFromCart} className="white-button">
                        <i
                          className="ri-shopping-basket-line"
                          style={{ color: "var(--dms-color-text-muted)" }}
                        ></i>
                        Remove from Cart
                      </Button>
                    )}
                    {(canDownloadOriginals &&
                      canDownloadOriginals.length > 0) && width >= 1080 ? (
                        <Button
                          className="white-button"
                          onClick={() =>
                            downloadSelected(canDownloadOriginals, 1)
                          }
                        >
                          <i
                            className="ri-file-download-line"
                            style={{ color: "#0860DB" }}
                          ></i>
                          Download <b>Natives</b>
                        </Button>
                      ) : null}
                    {canDownloadPdfs && canDownloadPdfs.length > 0 ? (
                      <Button
                        className="white-button"
                        onClick={() => downloadSelected(canDownloadPdfs, 2)}
                      >
                        <img src={pdfLineIcon} alt="PDF" className="pdf-icon" />
                        Download <b>PDF</b>
                      </Button>
                    ) : null}
                    {canDownloadPdfs && canDownloadPdfs.length > 0 && (!showMoreActions ||
                      width >= 1800) ? (
                      <Button
                        className="white-button"
                        onClick={handleDownloadBook}
                      >
                        <i
                          className="ri-book-line"
                          style={{ color: "var(--dms-color-book-action)" }}
                        ></i>
                        Download as <b>Book</b>
                      </Button>
                    ) : null}
                    {showMoreActions ? (
                      <Dropdown
                        placement="top"
                        className="white-button"
                        menu={{ items: getMenuItems(), className: "document-actions-menu" }}
                      >
                        <Button className="white-button">
                          {width < 768 ? "Select action" : "More"}
                          <i className="ri-arrow-down-s-line"></i>
                        </Button>
                      </Dropdown>
                    ) : null}
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </div>
      </>
    );
  } else if (
    countOfSelectedItems === 0 &&
    isCartTab &&
    cartDocumentIds.length > 0
  ) {
    return (
      <>
        {contextHolder}
        <div
          style={{ zIndex: 2, width: "100%" }}
          className={
            countOfSelectedItems
              ? "footer-for-select bottom-0"
              : "footer-for-select bottom-0 zero-selected"
          }
        >
          <div className="row">
            <Row className="footer-for-select-row" style={{ padding: 0 }}>
              <Col>
                <Button
                  onClick={showQuestionDeleteCartList}
                  className="common-button"
                >
                  <i className="ri-delete-bin-4-line" />
                  Empty Cart
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      </>
    );
  } else {
    return null;
  }
};

export default DashboardTableActionsComponent;
